/**
 * React hook for saved searches on the Search page, backed by the users DAO
 * via the UserPreference GraphQL API.
 *
 * This replaces the localStorage-based `savedSearchUtils.ts` functions with a
 * hook that fetches/writes through the backend. Components consuming this hook
 * get reactive updates from the Apollo cache — no custom events needed.
 */

import { UserPreferenceKey } from 'src/generated/graphql';
import { useUserPreferences } from 'src/common/savedSearches/useUserPreferences';
import { SearchState } from '../SearchPage';
import {
  isValidSortState,
  DEFAULT_SORT_STATE,
  removeSourceOnlySortField,
  SortState,
} from '../sortState';
import { SavedSearchEntry } from 'src/common/savedSearches';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

export type SavedSearch = SavedSearchEntry<SearchState>;

/** Maximum number of saved searches allowed. */
export const MAX_SAVED_SEARCHES = 5;

/**
 * Validates a saved SearchState, ensuring sort state is current.
 * Returns null if the state contains unrecognized values (entry will be dropped).
 */
function parseSearchState(state: unknown): SearchState | null {
  if (!state || typeof state !== 'object') return null;

  const s = state as Record<string, unknown>;

  if (typeof s.queryText !== 'string') return null;
  if (s.mode !== 'SOURCE' && s.mode !== 'TRANSLATION') return null;
  if (typeof s.exactMatch !== 'boolean') return null;

  const sortState: SortState = isValidSortState(s.sortState)
    ? s.sortState
    : DEFAULT_SORT_STATE;

  const filters =
    s.filters && typeof s.filters === 'object'
      ? (s.filters as SearchState['filters'])
      : {};

  return {
    queryText: s.queryText as string,
    mode: s.mode as SearchState['mode'],
    exactMatch: s.exactMatch as boolean,
    filters,
    sortState:
      s.mode === 'TRANSLATION'
        ? removeSourceOnlySortField(sortState)
        : sortState,
  };
}

/**
 * The name suggested for a saved search. Only the no-criteria fallback is localized; the rest is
 * the user's query text and TRANSCOM identifiers, interpolated verbatim. Only ever a SUGGESTION —
 * once saved the label is stored verbatim and never re-derived.
 */
export function generateLabel(t: TranslateFn, state: SearchState): string {
  const parts: string[] = [];
  if (state.queryText) {
    parts.push(`"${state.queryText}"`);
  }
  if (state.filters.namespaceIds?.length) {
    parts.push(`ns:${state.filters.namespaceIds.join(',')}`);
  }
  if (state.mode === 'TRANSLATION' && state.filters.translatedLocales?.length) {
    parts.push(`locale:${state.filters.translatedLocales.join(',')}`);
  }
  if (parts.length === 0) {
    parts.push(
      state.mode === 'SOURCE'
        ? t('search-saved-label-source-browse')
        : t('search-saved-label-translation-browse'),
    );
  }
  return parts.join(' ').slice(0, 50);
}

/**
 * Hook providing saved searches for the Search page. Fetches from and persists
 * to the backend via GraphQL. Returns reactive data from the Apollo cache.
 *
 * The backend stores SOURCE and TRANSLATION entries together under one key with
 * a combined limit of 5; all entries are always visible regardless of the
 * currently selected mode tab.
 */
export function useSavedSearches() {
  return useUserPreferences<SearchState>({
    preferenceKey: UserPreferenceKey.SavedSearches,
    parseState: parseSearchState,
    generateLabel,
    maxEntries: MAX_SAVED_SEARCHES,
  });
}
